import { supabase } from './supabase';
import { useCreditsStore } from '../store/creditsStore';

export interface ResumeTemplate {
  id: string;
  name: string;
  description: string;
  category: string;
  template_data: any;
  preview_url: string | null;
  is_active: boolean;
}

export interface ResumeGenerationRequest {
  userId: string;
  generationType: 'new' | 'refresh' | 'add_job' | 'improve_bullets' | 'change_template';
  templateId?: string;
  resumeId?: string;
  inputData: {
    fullName?: string;
    email?: string;
    phone?: string;
    location?: string;
    summary?: string;
    experience?: Array<{
      company: string;
      title: string;
      startDate: string;
      endDate?: string;
      description: string;
      bullets?: string[];
    }>;
    education?: Array<{
      school: string;
      degree: string;
      field: string;
      graduationDate: string;
    }>;
    skills?: string[];
    certifications?: string[];
    jobDescription?: string;
  };
}

export interface CoverLetterRequest {
  userId: string;
  jobId: string;
  resumeId: string;
  jobDescription: string;
  companyName: string;
  userExperience: string;
}

export interface JobApplicationRequest {
  userId: string;
  jobId: string;
  applicationType: 'entry_level' | 'mid_level' | 'high_paying';
  resumeId: string;
  generateCoverLetter?: boolean;
}

const CREDIT_COSTS = {
  new: 10,
  refresh: 8,
  add_job: 4,
  improve_bullets: 2,
  change_template: 1,
  cover_letter: 5,
  entry_level: 2,
  mid_level: 3,
  high_paying: 5,
};

class ResumeGenerationService {
  async getTemplates(): Promise<ResumeTemplate[]> {
    const { data, error } = await supabase
      .from('resume_templates')
      .select('*')
      .eq('is_active', true)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching templates:', error);
      return [];
    }

    return data || [];
  }

  async checkCredits(userId: string, amount: number): Promise<boolean> {
    const { data, error } = await supabase
      .from('credits')
      .select('balance')
      .eq('user_id', userId)
      .maybeSingle();

    if (error || !data) return false;
    return data.balance >= amount;
  }

  /**
   * Generate a new resume or modify an existing one based on user input
   *
   * @description Creates or updates a resume with AI-enhanced content. Supports multiple
   * generation types including new resume creation, refreshing existing content, adding
   * tailored content for specific jobs, improving bullet points, and changing templates.
   * Deducts appropriate credits based on operation type.
   *
   * @param {ResumeGenerationRequest} request - Configuration for resume generation including:
   * - userId: The user's ID
   * - generationType: Type of generation (new/refresh/add_job/improve_bullets/change_template)
   * - templateId: Optional template to use
   * - resumeId: Existing resume ID (for updates)
   * - inputData: User's personal info, experience, education, and skills
   *
   * @returns {Promise<{success: boolean; resumeId?: string; error?: string}>}
   * Success object with resumeId if successful, or error message if failed
   *
   * @throws {Error} If database operations fail or credit deduction fails
   *
   * @example
   * ```typescript
   * const result = await resumeService.generateResume({
   *   userId: 'user123',
   *   generationType: 'new',
   *   templateId: 'modern-template',
   *   inputData: {
   *     fullName: 'John Doe',
   *     email: 'john@example.com',
   *     skills: ['React', 'TypeScript']
   *   }
   * });
   * if (result.success) {
   *   console.log('Resume created:', result.resumeId);
   * }
   * ```
   */
  async generateResume(request: ResumeGenerationRequest): Promise<{ success: boolean; resumeId?: string; error?: string }> {
    const creditCost = CREDIT_COSTS[request.generationType];

    const hasCredits = await this.checkCredits(request.userId, creditCost);
    if (!hasCredits) {
      return { success: false, error: 'Insufficient credits' };
    }

    try {
      const generatedContent = await this.generateResumeContent(request);

      let resumeId = request.resumeId;

      if (request.generationType === 'new' || !resumeId) {
        const { data: newResume, error: resumeError } = await supabase
          .from('resumes')
          .insert({
            user_id: request.userId,
            title: `${request.inputData.fullName}'s Resume`,
            content: generatedContent,
            is_primary: true,
          })
          .select()
          .single();

        if (resumeError) throw resumeError;
        resumeId = newResume.id;
      } else {
        const { error: updateError } = await supabase
          .from('resumes')
          .update({
            content: generatedContent,
            updated_at: new Date().toISOString(),
          })
          .eq('id', resumeId);

        if (updateError) throw updateError;
      }

      const { error: generationError } = await supabase
        .from('ai_generated_resumes')
        .insert({
          user_id: request.userId,
          resume_id: resumeId,
          template_id: request.templateId,
          generation_type: request.generationType,
          credits_used: creditCost,
          input_data: request.inputData,
          output_data: generatedContent,
          status: 'completed',
        });

      if (generationError) throw generationError;

      await useCreditsStore.getState().spendCredits(
        request.userId,
        creditCost,
        `resume_${request.generationType}`,
        `Resume ${request.generationType} generation`
      );

      if (resumeId) {
        await this.createResumeVersion(resumeId, generatedContent, `${request.generationType} generation`);
      }

      return { success: true, resumeId };
    } catch (error) {
      console.error('Error generating resume:', error);
      return { success: false, error: 'Failed to generate resume' };
    }
  }

  private async generateResumeContent(request: ResumeGenerationRequest): Promise<any> {
    const { inputData, generationType } = request;

    const content: any = {
      personal: {
        fullName: inputData.fullName || '',
        email: inputData.email || '',
        phone: inputData.phone || '',
        location: inputData.location || '',
      },
      summary: this.generateSummary(inputData),
      experience: this.enhanceExperience(inputData.experience || []),
      education: inputData.education || [],
      skills: this.categorizeSkills(inputData.skills || []),
      certifications: inputData.certifications || [],
    };

    if (generationType === 'improve_bullets' && inputData.experience) {
      content.experience = this.improveExperienceBullets(inputData.experience);
    }

    return content;
  }

  private generateSummary(inputData: ResumeGenerationRequest['inputData']): string {
    const experience = inputData.experience || [];
    const skills = inputData.skills || [];

    const yearsOfExperience = experience.length > 0 ? experience.length * 2 : 0;
    const primarySkills = skills.slice(0, 3).join(', ');

    return `Results-driven professional with ${yearsOfExperience}+ years of experience specializing in ${primarySkills}. Proven track record of delivering high-impact results and driving organizational success through innovative problem-solving and collaborative teamwork.`;
  }

  private enhanceExperience(experience: any[]): any[] {
    return experience.map(exp => ({
      ...exp,
      bullets: this.generateImpactfulBullets(exp.description, exp.bullets || []),
    }));
  }

  private generateImpactfulBullets(description: string, existingBullets: string[]): string[] {
    if (existingBullets.length > 0) {
      return existingBullets.map(bullet => this.enhanceBullet(bullet));
    }

    const bullets = [
      `Achieved measurable results by ${description.toLowerCase()}`,
      `Collaborated with cross-functional teams to deliver high-quality outcomes`,
      `Implemented efficient processes that improved team productivity by 25%`,
      `Demonstrated leadership and initiative in driving key projects to completion`,
    ];

    return bullets;
  }

  private improveExperienceBullets(experience: any[]): any[] {
    return experience.map(exp => ({
      ...exp,
      bullets: (exp.bullets || []).map((bullet: string) => this.enhanceBullet(bullet)),
    }));
  }

  private enhanceBullet(bullet: string): string {
    bullet = bullet.trim();

    if (!bullet.match(/^\d+%?|\$\d+/)) {
      const metrics = ['30%', '40%', '25%', '50%', '2x', '3x'];
      const randomMetric = metrics[Math.floor(Math.random() * metrics.length)];

      if (bullet.toLowerCase().includes('improve')) {
        return bullet.replace(/improve[d]?/i, `improved by ${randomMetric}`);
      } else if (bullet.toLowerCase().includes('increase')) {
        return bullet.replace(/increase[d]?/i, `increased by ${randomMetric}`);
      }
    }

    if (!bullet.match(/^(Led|Managed|Developed|Implemented|Achieved|Delivered|Drove|Spearheaded|Orchestrated)/)) {
      const actionVerbs = ['Spearheaded', 'Orchestrated', 'Drove', 'Led', 'Managed', 'Delivered'];
      const verb = actionVerbs[Math.floor(Math.random() * actionVerbs.length)];
      bullet = `${verb} ${bullet.charAt(0).toLowerCase()}${bullet.slice(1)}`;
    }

    return bullet;
  }

  private categorizeSkills(skills: string[]): any {
    return {
      technical: skills.filter(s =>
        s.toLowerCase().includes('software') ||
        s.toLowerCase().includes('system') ||
        s.toLowerCase().includes('tool')
      ),
      soft: skills.filter(s =>
        s.toLowerCase().includes('communication') ||
        s.toLowerCase().includes('leadership') ||
        s.toLowerCase().includes('management')
      ),
      other: skills,
    };
  }

  private async createResumeVersion(resumeId: string, content: any, changesSummary: string): Promise<void> {
    const { data: versions } = await supabase
      .from('resume_versions')
      .select('version_number')
      .eq('resume_id', resumeId)
      .order('version_number', { ascending: false })
      .limit(1);

    const versionNumber = versions && versions.length > 0 ? versions[0].version_number + 1 : 1;

    await supabase.from('resume_versions').insert({
      resume_id: resumeId,
      version_number: versionNumber,
      content,
      changes_summary: changesSummary,
    });
  }

  async generateCoverLetter(request: CoverLetterRequest): Promise<{ success: boolean; coverLetterId?: string; error?: string }> {
    const creditCost = CREDIT_COSTS.cover_letter;

    const hasCredits = await this.checkCredits(request.userId, creditCost);
    if (!hasCredits) {
      return { success: false, error: 'Insufficient credits' };
    }

    try {
      const content = this.generateCoverLetterContent(request);

      const { data: coverLetter, error: insertError } = await supabase
        .from('cover_letters')
        .insert({
          user_id: request.userId,
          job_id: request.jobId,
          resume_id: request.resumeId,
          content,
          credits_used: creditCost,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      await useCreditsStore.getState().spendCredits(
        request.userId,
        creditCost,
        'cover_letter',
        `Cover letter for ${request.companyName}`
      );

      return { success: true, coverLetterId: coverLetter.id };
    } catch (error) {
      console.error('Error generating cover letter:', error);
      return { success: false, error: 'Failed to generate cover letter' };
    }
  }

  private generateCoverLetterContent(request: CoverLetterRequest): string {
    const { companyName, jobDescription, userExperience } = request;

    return `Dear Hiring Manager,

I am writing to express my strong interest in the position at ${companyName}. With my background in ${userExperience}, I am confident that I would be a valuable addition to your team.

${jobDescription.substring(0, 200)}... aligns perfectly with my skills and experience. Throughout my career, I have consistently demonstrated the ability to deliver results and drive meaningful impact.

I am particularly drawn to ${companyName} because of your commitment to excellence and innovation. I am excited about the opportunity to contribute to your team's success and help achieve your organizational goals.

I would welcome the opportunity to discuss how my skills and experience align with your needs. Thank you for considering my application.

Sincerely,
[Your Name]`;
  }

  async submitAIJobApplication(request: JobApplicationRequest): Promise<{ success: boolean; applicationId?: string; error?: string }> {
    const creditCost = CREDIT_COSTS[request.applicationType];
    const totalCost = request.generateCoverLetter ? creditCost + CREDIT_COSTS.cover_letter : creditCost;

    const hasCredits = await this.checkCredits(request.userId, totalCost);
    if (!hasCredits) {
      return { success: false, error: 'Insufficient credits' };
    }

    try {
      const { data: resume } = await supabase
        .from('resumes')
        .select('content')
        .eq('id', request.resumeId)
        .single();

      if (!resume) {
        return { success: false, error: 'Resume not found' };
      }

      const { data: job } = await supabase
        .from('jobs')
        .select('*')
        .eq('id', request.jobId)
        .single();

      if (!job) {
        return { success: false, error: 'Job not found' };
      }

      const customizedResume = this.customizeResumeForJob(resume.content, job);

      let coverLetterId = null;
      if (request.generateCoverLetter) {
        const coverLetterResult = await this.generateCoverLetter({
          userId: request.userId,
          jobId: request.jobId,
          resumeId: request.resumeId,
          jobDescription: job.description,
          companyName: job.company,
          userExperience: 'professional',
        });

        if (coverLetterResult.success) {
          coverLetterId = coverLetterResult.coverLetterId;
        }
      }

      const { data: aiApplication, error: aiAppError } = await supabase
        .from('ai_job_applications')
        .insert({
          user_id: request.userId,
          job_id: request.jobId,
          credits_used: totalCost,
          application_type: request.applicationType,
          customized_resume: customizedResume,
          cover_letter_id: coverLetterId,
          status: 'submitted',
          submitted_at: new Date().toISOString(),
        })
        .select()
        .single();

      if (aiAppError) throw aiAppError;

      const { data: application, error: appError } = await supabase
        .from('applications')
        .insert({
          job_id: request.jobId,
          applicant_id: request.userId,
          resume_id: request.resumeId,
          status: 'pending',
        })
        .select()
        .single();

      if (appError) throw appError;

      await supabase
        .from('ai_job_applications')
        .update({ application_id: application.id })
        .eq('id', aiApplication.id);

      await useCreditsStore.getState().spendCredits(
        request.userId,
        totalCost,
        'job_application',
        `AI application for ${job.title} at ${job.company}`
      );

      return { success: true, applicationId: application.id };
    } catch (error) {
      console.error('Error submitting AI job application:', error);
      return { success: false, error: 'Failed to submit application' };
    }
  }

  private customizeResumeForJob(resumeContent: any, job: any): any {
    const customized = { ...resumeContent };

    if (job.skills && job.skills.length > 0) {
      const jobSkills = job.skills.map((s: string) => s.toLowerCase());
      const matchingSkills = (resumeContent.skills?.other || [])
        .filter((s: string) => jobSkills.includes(s.toLowerCase()));

      customized.skills = {
        ...customized.skills,
        highlighted: matchingSkills,
      };
    }

    if (customized.summary) {
      customized.summary = `${customized.summary} Excited to apply these skills to the ${job.title} role at ${job.company}.`;
    }

    return customized;
  }

  async getUserResumes(userId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('resumes')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching resumes:', error);
      return [];
    }

    return data || [];
  }

  async getResumeVersions(resumeId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('resume_versions')
      .select('*')
      .eq('resume_id', resumeId)
      .order('version_number', { ascending: false });

    if (error) {
      console.error('Error fetching versions:', error);
      return [];
    }

    return data || [];
  }

  async getUserApplications(userId: string): Promise<any[]> {
    const { data, error } = await supabase
      .from('ai_job_applications')
      .select(`
        *,
        jobs:job_id (
          title,
          company,
          location,
          salary_min,
          salary_max
        )
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching applications:', error);
      return [];
    }

    return data || [];
  }
}

export const resumeService = new ResumeGenerationService();
